import React, { useEffect } from 'react';
import { ToolItem } from '../types';
import { getSeoMetadata } from '../utils/seoConfig';
import { SUPPORTED_LANGUAGES, buildLangPath, getNormalizedPath } from '../i18n/languages';

interface SeoHeadProps {
  activeNav: string;
  activeQuickTool: ToolItem | null;
}

export const SeoHead: React.FC<SeoHeadProps> = ({ activeNav, activeQuickTool }) => {
  useEffect(() => {
    const meta = getSeoMetadata(activeNav, activeQuickTool);

    // 1. Update Title
    document.title = meta.title;

    // Helper to safely set or create meta tag
    const setMetaTag = (selector: string, attrName: string, attrVal: string, content: string) => {
      let element = document.head.querySelector(selector) as HTMLMetaElement | null;
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attrName, attrVal);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    // Helper to set link tags (e.g. canonical)
    const setLinkTag = (rel: string, href: string) => {
      let element = document.head.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
      if (!element) {
        element = document.createElement('link');
        element.setAttribute('rel', rel);
        document.head.appendChild(element);
      }
      element.setAttribute('href', href);
    };

    // 2. Standard Meta Tags
    setMetaTag('meta[name="description"]', 'name', 'description', meta.description);
    setMetaTag('meta[name="keywords"]', 'name', 'keywords', meta.keywords);
    setMetaTag('meta[name="robots"]', 'name', 'robots', 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');
    setMetaTag('meta[name="author"]', 'name', 'author', 'Dualis Tools');

    // 3. Open Graph Meta Tags
    setMetaTag('meta[property="og:title"]', 'property', 'og:title', meta.ogTitle);
    setMetaTag('meta[property="og:description"]', 'property', 'og:description', meta.ogDescription);
    setMetaTag('meta[property="og:type"]', 'property', 'og:type', meta.ogType);
    setMetaTag('meta[property="og:url"]', 'property', 'og:url', meta.canonicalUrl);
    setMetaTag('meta[property="og:site_name"]', 'property', 'og:site_name', 'Dualis Tools');
    setMetaTag('meta[property="og:image"]', 'property', 'og:image', meta.ogImage);

    // 4. Twitter Card Meta Tags
    setMetaTag('meta[name="twitter:card"]', 'name', 'twitter:card', 'summary_large_image');
    setMetaTag('meta[name="twitter:title"]', 'name', 'twitter:title', meta.ogTitle);
    setMetaTag('meta[name="twitter:description"]', 'name', 'twitter:description', meta.ogDescription);
    setMetaTag('meta[name="twitter:image"]', 'name', 'twitter:image', meta.ogImage);

    // 5. Canonical Link
    setLinkTag('canonical', meta.canonicalUrl);

    // 6. Structured Data (JSON-LD)
    const existingScript = document.head.querySelector('#dualis-jsonld-schema');
    if (existingScript) {
      existingScript.remove();
    }

    const scriptElement = document.createElement('script');
    scriptElement.id = 'dualis-jsonld-schema';
    scriptElement.type = 'application/ld+json';
    scriptElement.textContent = JSON.stringify(meta.jsonLd);
    document.head.appendChild(scriptElement);

  }, [activeNav, activeQuickTool]);

  return null; // Side-effect component only, no visible UI elements created
};
